import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './Button';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  error: Error | null;
}

/** Prevents a single page crash from becoming a blank white screen. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('UI crash:', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            minHeight: '100dvh',
            display: 'grid',
            placeItems: 'center',
            padding: 24,
            background: 'var(--color-fog, #F1F3F5)',
            fontFamily: 'var(--font-body, system-ui)',
          }}
        >
          <div style={{ maxWidth: 420, textAlign: 'center' }}>
            <h1 style={{ fontFamily: 'var(--font-display, system-ui)', color: 'var(--color-ink, #0E1216)' }}>
              {this.props.fallbackTitle ?? 'Something went wrong'}
            </h1>
            <p style={{ color: 'var(--color-steel, #8A939B)', margin: '12px 0 20px' }}>
              {this.state.error.message || 'The page failed to render.'}
            </p>
            <Button
              onClick={() => {
                this.setState({ error: null });
                window.location.assign('/app/dispatch');
              }}
            >
              Back to dispatch
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                this.setState({ error: null });
                window.location.assign('/login');
              }}
            >
              Sign in again
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
